<template>
  <div>
    <!-- data table -->
    <v-data-table
      class="elevation-3 rounded-t-0"
      :items-per-page="page_count"
      :server-items-length="page_count"
      :loading="loading"
      loading-text="Please wait.... Loading"
      :headers="headers"
      :items="items"
      hide-default-footer
      mobile-breakpoint
      @dblclick:row="onItemDoubleClick"
      @contextmenu:row="onRightClick"
    >
      <template v-slot:[`item.case_id`]="{ item }">
        <div
          class="mb-1"
          v-if="
            [
              $keys.WEB,
              $keys.ANDROID,
              $keys.IOS,
              $keys.WHATSAPP,
              $keys.CALL,
            ].includes(item.source)
          "
        >
          <v-icon size="20" color="primary" v-if="item.source === $keys.WEB">
            mdi-web
          </v-icon>
          <v-icon size="20" color="green" v-if="item.source === $keys.ANDROID">
            mdi-android
          </v-icon>
          <v-icon size="20" color="black" v-if="item.source === $keys.IOS">
            mdi-apple
          </v-icon>
          <v-icon size="20" color="green" v-if="item.source === $keys.WHATSAPP">
            mdi-whatsapp
          </v-icon>
          <v-icon size="20" color="black" v-if="item.source === $keys.CALL">
            mdi-phone
          </v-icon>
        </div>
        {{ item.case_id }}
        <v-chip
          small
          pill
          class="mt-1"
          v-if="item.delayed_days > 0"
          color="red"
          outlined
        >
          Delayed by {{ item.delayed_days }} days
        </v-chip>
      </template>

      <template v-slot:[`item.date`]="{ item }">
        <div class="my-4">
          <div v-if="item.modified">
            {{ $utils.getDateFormat(item.modified) }}
          </div>
          <div v-else><v-icon>mdi-minus</v-icon></div>
        </div>
      </template>

      <template v-slot:[`item.client`]="{ item }">
        <div class="py-4" v-if="item.client">
          {{ item.client.name }}
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.contacts`]="{ item }">
        <div class="py-4 justify-contain" v-if="item.client">
          <div>{{ item.client.user.email }}</div>
          <div>{{ item.client.user.mobile_display }}</div>
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.service`]="{ item }">
        <div class="py-4" v-if="item.service">
          {{ item.service.name }}
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.university`]="{ item }">
        <div class="py-4" v-if="item.university">
          {{ item.university.name }}
          <div>
            {{ item.university.address }}
            <span v-if="item.university.city">
              {{ item.university.city.name }},
              <span>{{ item.university.city.state.name }}</span>
            </span>
          </div>
        </div>
      </template>

      <template v-slot:[`item.eca`]="{ item }">
        <div class="py-4" v-if="item.eca.length">
          <div v-for="eca in item.eca" :key="`eca-${eca.id}`">
            {{ eca.name }}
          </div>
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.total`]="{ item }">
        <div class="py-4 d-flex align-center justify-center">
          <span class="mr-2">₹{{ item.total_amount }}</span>
          <v-tooltip bottom v-if="item.total_amount">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="pointer">
                mdi-information
              </v-icon>
            </template>
            <div>
              <div v-if="item.total_university_cost">
                University Cost: ₹{{ item.total_university_cost }}
              </div>
              <div>Processing Cost: ₹{{ item.total_processing_cost }}</div>
              <div>Shipping Cost: ₹{{ item.shipping_cost }}</div>
              <div>GST Amount: ₹{{ item.gst_amount }}</div>
              <div>Discount Amount: ₹{{ item.discount_amount }}</div>
            </div>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.assigned_sales`]="{ item }">
        <div v-if="item.sales">
          {{ item.sales.name }}
        </div>

        <!-- assign sales -->
        <div
          class="pointer primary--text"
          @click="assignSales(item.id)"
          v-if="item.actions.includes($keys.ASSIGN_SALES)"
        >
          <v-icon color="primary" size="14"> mdi-account-plus </v-icon>
          <span class="executive ml-1">Assign Sales </span>
        </div>

        <!-- change sales -->
        <div
          class="pointer d-flex justify-center align-end primary--text mb-1"
          @click="assignSales(item.id)"
          v-if="item.actions.includes($keys.CHANGE_SALES)"
        >
          <v-icon color="primary" size="14"> mdi-account-edit </v-icon>
          <span class="executive ml-1">Change Sales </span>
        </div>

        <!-- unassign me -->
        <div
          class="pointer d-flex justify-center align-end primary--text"
          @click="unAssignSales(item.id)"
          v-if="item.actions.includes($keys.UNASSIGN_ME)"
        >
          <v-icon color="primary" size="14"> mdi-account-minus </v-icon>
          <span class="executive ml-1">Unassign me</span>
        </div>

        <!-- unassign sales -->
        <div
          class="pointer d-flex justify-center align-end primary--text"
          @click="unAssignSales(item.id)"
          v-if="item.actions.includes($keys.UNASSIGN_SALES)"
        >
          <v-icon color="primary" size="14"> mdi-account-minus </v-icon>
          <span class="executive ml-1">Unassign Sales</span>
        </div>
      </template>

      <template v-slot:[`item.assigned_operations`]="{ item }">
        <div v-if="item.operations">
          {{ item.operations.name }}
        </div>
        <v-icon v-else>mdi-minus</v-icon>
      </template>

      <template v-slot:[`item.follow_up_date`]="{ item }">
        <div class="py-4" v-if="item.follow_up_date">
          <div>{{ item.follow_up_date }}</div>
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.follow_up_reason`]="{ item }">
        <div class="py-4" v-if="item.follow_up">
          <div>{{ item.follow_up.reason }}</div>
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.follow_up_reason_date`]="{ item }">
        <div class="py-4" v-if="item.follow_up">
          {{ $utils.getDateFormat(item.follow_up.follow_up_date) }}
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <div class="py-4" v-if="item.status">
          <div>{{ item.status_display }}</div>
        </div>
        <div class="py-4" v-else>
          <span><v-icon>mdi-minus</v-icon></span>
        </div>
      </template>

      <template v-slot:[`item.payment_status`]="{ item }">
        <v-chip
          class="text-capitalize lighten-5 text--darken-2 font-weight-bold"
          :class="
            item.payment_status == 'successful'
              ? 'success success--text'
              : item.payment_status == 'pending'
              ? 'blue blue--text'
              : 'error error--text'
          "
        >
          {{ item.payment_status_display }}
        </v-chip>
      </template>

      <template v-slot:[`item.documents`]="{ item }">
        <div v-if="item.documents.length" class="my-2">
          <div v-for="(list, index) in item.documents" :key="index">
            <div>
              {{ list.title }} -
              <span v-if="list.status == 'pending'" class="blue--text bold">
                {{ list.status_display }}</span
              >
              <span v-if="list.status == 'uploaded'" class="orange--text bold">
                {{ list.status_display }}</span
              >
              <span v-if="list.status == 'verified'" class="green--text bold">
                {{ list.status_display }}</span
              >
              <span v-if="list.status == 'rejected'" class="red--text bold">
                {{ list.status_display }}</span
              >
              <span v-if="list.status == 'downloaded'" class="gray--text bold">
                {{ list.status_display }}</span
              >
              <span v-if="list.status == 'removed'" class="red--text bold">
                {{ list.status_display }}</span
              >
            </div>
          </div>
        </div>
        <span v-else><v-icon>mdi-minus</v-icon></span>
      </template>

      <template v-slot:[`item.action`]="{ item }">
        <div class="d-flex justify-center" style="gap: 10px">
          <v-btn
            small
            outlined
            color="primary"
            class="text-capitalize"
            v-for="action in btnActions(item)"
            :key="action.key"
            @click="actionHandler(action.key, item)"
          >
            <v-icon left v-if="action.icon">mdi-{{ action.icon }}</v-icon>
            {{ action.text }}
          </v-btn>

          <v-menu
            left
            offset-y
            min-width="200"
            width="200"
            nudge-bottom="10"
            v-if="menuActions(item).length"
          >
            <template v-slot:activator="{ attrs, on }">
              <v-btn
                small
                outlined
                color="primary"
                class="text-capitalize"
                width="5px"
                min-width="5px"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list nav dense :outlined="$vuetify.theme.dark">
              <v-list-item
                link
                v-for="action in menuActions(item)"
                :key="action.key"
                @click="actionHandler(action.key, item)"
              >
                <v-list-item-title>{{ action.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-data-table>

    <!-- pagination -->
    <v-container
      class="d-flex justify-space-between align-center px-0 mb-0 mt-4"
      fluid
    >
      <div class="d-flex align-center text-subtitle-2">
        <div class="mr-2">Rows per page</div>
        <div style="width: 75px">
          <v-select
            :menu-props="{ bottom: true, offsetY: true }"
            :items="itemsPerPageList"
            v-model="page_count"
            hide-details
            dense
            solo
            color="primary"
          ></v-select>
        </div>
        <div class="ml-6">
          Results: {{ pageStart }} - {{ pageEnd }} of {{ total_count }}
        </div>
      </div>
      <v-pagination
        v-model="page_number"
        :length="page_total"
        :total-visible="5"
        color="primary"
      >
      </v-pagination>
    </v-container>

    <!-- right click menu -->
    <v-menu
      v-model="rightClickMenu.show"
      :position-x="rightClickMenu.positionX"
      :position-y="rightClickMenu.positionY"
      transition="scale-transition"
      min-width="200"
      width="200"
      absolute
      offset-y
      v-if="
        rightClickMenu.selectedItem &&
        actions(rightClickMenu.selectedItem).length
      "
    >
      <v-list nav dense :outlined="$vuetify.theme.dark">
        <v-list-item
          link
          v-for="action in actions(rightClickMenu.selectedItem)"
          :key="action.key"
          @click="actionHandler(action.key, rightClickMenu.selectedItem)"
        >
          <v-list-item-title>{{ action.text }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- send quotation dialog -->
    <send-quotation-dialog @submitted="reloadData" />

    <!-- move to follow-up dialog -->
    <CaseCommonDialog @submitted="reloadData" />

    <select-executive-modal @selected="onExecutiveSelected" />
    <AddUpdatePayment @submitted="reloadData" />
  </div>
</template>
<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";

import header from "@/common/caseManagementHeader/caseManagementHeader";
import CaseCommonDialog from "@/components/Dialogs/CaseCommonDialog";

import SelectExecutiveModal from "@/components/selectExecutiveModal.vue";
import SendQuotationDialog from "@/components/Dialogs/SendQuotationDialog";
import AddUpdatePayment from "@/components/CaseManagement/Payment/AddUpdateDialog";

export default {
  components: {
    CaseCommonDialog,
    SelectExecutiveModal,
    SendQuotationDialog,
    AddUpdatePayment,
  },
  data() {
    return {
      items: [],
      loading: false,

      page_total: 1,
      page_number: 1,
      total_count: 0,
      itemsPerPageList: [5, 10, 15, 20, 30, 40, 50],
      page_count: 10,

      rightClickMenu: {
        show: false,
        positionX: 0,
        positionY: 0,
        selectedItem: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      getUserDetail: "getUser",
      search: "caseManagement/search",
      filters: "caseManagement/filters",
      activeTab: "caseManagement/activeTab",
      getExecutiveModal: "caseManagement/getExecutiveModal",
    }),

    headers() {
      switch (this.activeTab.key) {
        case "open":
        case "assigned_to_me":
        case "assigned_to_sales":
          return header.open_case;
        case "quotation_sent":
          return header.assigned;
        case "follow_up":
          return header.follow_up;
        case "payment_confirmation":
          return header.payment_confirmation;
        case "converted":
          return header.converted;
        case "irrelevant":
          return header.irrelevant;
        case "sent_to_eca":
        case "downloaded_by_eca":
        case "viewed_by_eca":
          return header.eca_header;
        default:
          return null;
      }
    },

    pageStart() {
      let start = (this.page_number - 1) * this.page_count + 1;
      if (start + 1 > this.total_count) start = this.total_count;
      return start;
    },

    pageEnd() {
      let end = this.pageStart + this.page_count - 1;
      if (end > this.total_count) end = this.total_count;
      return end;
    },
  },
  watch: {
    page_count() {
      this.getCaseList();
    },

    page_number() {
      this.getCaseList();
      this.updateRoute();
    },

    activeTab: {
      handler() {
        this.reloadData();
      },
      deep: true,
    },

    search() {
      this.debouncedReloadData();
    },

    filters: {
      handler() {
        this.reloadData();
        this.updateRoute();
      },
      deep: true,
    },
  },

  created() {
    const query = this.$route.query;

    if (query.page) {
      this.page_number = Number(query.page);
    }

    if (query.search) {
      this.setSearch(String(query.search));
    }

    if (query.executive) {
      this.setFilters({ executive: Number(query.executive) });
    }

    if (query.date) {
      this.setFilters({ date: [String(query.date)] });
    }

    if (query.start_date && query.end_date) {
      this.setFilters({
        date: [String(query.start_date), String(query.end_date)],
      });
    }

    this.updateRoute();
  },

  mounted() {
    this.getCaseList();
  },

  methods: {
    ...mapActions({
      showSnackbar: "snackBar/showSnackbar",
      setSearch: "caseManagement/setSearch",
      setFilters: "caseManagement/setFilters",
      executiveModal: "caseManagement/executiveModal",
      toggleSendQuotationDialog: "dialogs/toggleSendQuotationDialog",
      toggleCaseCommonDialog: "dialogs/toggleCaseCommonDialog",
      toggleAddPaymentDialog: "payments/toggleAddUpdateDialog",
    }),

    debouncedReloadData: _.debounce(function () {
      this.reloadData();
      this.updateRoute();
    }, 1000),

    reloadData() {
      this.$emit("reloadData");
      this.getCaseList();
    },

    updateRoute() {
      const query = {};

      if (this.search) {
        query.search = String(this.search);
      }

      if (this.page_number) {
        query.page = Number(this.page_number);
      }

      if (this.filters.executive) {
        query.executive = Number(this.filters.executive);
      }

      if (this.filters.date.length) {
        if (this.filters.date.length === 2) {
          query.start_date = String(this.filters.date[0]);
          query.end_date = String(this.filters.date[1]);
        } else {
          query.date = String(this.filters.date[0]);
        }
      }

      this.$router
        .replace({
          path: this.$route.path,
          query: query,
        })
        .catch((error) => error);
    },

    actions(item) {
      return item.btn_actions;
    },

    btnActions(item) {
      return this.$_.filter(item.btn_actions, { show_in_menu: false });
    },

    menuActions(item) {
      return this.$_.filter(item.btn_actions, { show_in_menu: true });
    },

    actionHandler(key, item) {
      switch (key) {
        case "view_case": {
          this.caseDetail(item.id);
          break;
        }

        case "add_payment": {
          this.toggleAddPaymentDialog({
            show: true,
            id: null,
            caseId: item.id,
            type: "add",
          });
          break;
        }

        case "send_quotation": {
          this.toggleSendQuotationDialog({
            show: true,
            caseId: item.id,
            clientId: item.client.id,
          });
          break;
        }

        case "move_to_follow_up": {
          this.toggleCaseCommonDialog({
            show: true,
            caseId: item.id,
            type: this.$keys.FOLLOW_UP,
          });
          break;
        }

        case "mark_as_irrelevant": {
          this.toggleCaseCommonDialog({
            show: true,
            caseId: item.id,
            type: this.$keys.IRRELEVANT,
          });
          break;
        }

        default:
          break;
      }
    },

    caseDetail(id) {
      this.$router.push({
        name: "caseDetail",
        params: {
          id: id,
        },
      });
    },

    assignSales(case_id) {
      this.executiveModal({
        id: case_id,
        show: true,
        type: "assign_sales",
      });
    },

    unAssignSales(case_id) {
      let data = {
        case: case_id,
      };

      const successHandler = (res) => {
        this.showSnackbar({
          text: res.data.message,
          color: "success",
        });
        this.reloadData();
      };

      this.$request(this.$keys.POST, this.$urls.case.unAssignSales, {
        data: data,
        onSuccess: successHandler,
      });
    },

    getCaseList() {
      this.items = [];
      this.loading = true;

      const onSuccess = (res) => {
        this.page_total = res.data.page_info.page_total;
        this.page_number = res.data.page_info.page_number;
        this.total_count = res.data.page_info.total_count;
        this.items = res.data.data;
      };

      const onFinally = () => {
        this.loading = false;
      };

      const params = {
        page_number: this.page_number,
        page_count: this.page_count,
        case_status: this.activeTab.key,
        search: this.search,
        executive_id: this.filters.executive,
        university_id: this.filters.university,
        eca_body_id: this.filters.eca,
      };

      if (this.filters.date) {
        if (this.filters.date.length === 2) {
          params["start_date"] = this.filters.date[0];
          params["end_date"] = this.filters.date[1];
        } else {
          params["date"] = this.filters.date[0];
        }
      }

      return this.$request(this.$keys.GET, this.$urls.case.list, {
        params,
        onSuccess,
        onFinally,
        cancel: true,
      });
    },

    onExecutiveSelected(selected_id) {
      const data = {
        case: this.getExecutiveModal.id,
        sales: selected_id,
      };
      const successHandler = () => {
        this.reloadData();
        this.executiveModal({
          id: "",
          show: false,
          type: "",
        });
      };
      this.$request(this.$keys.POST, this.$urls.case.assignSales, {
        data: data,
        onSuccess: successHandler,
      });
    },

    onItemDoubleClick(event, { item }) {
      this.caseDetail(item.id);
    },

    onRightClick(event, { item }) {
      event.preventDefault();
      this.rightClickMenu.selectedItem = item;
      this.rightClickMenu.show = false;
      this.rightClickMenu.positionX = event.clientX;
      this.rightClickMenu.positionY = event.clientY;
      this.$nextTick(() => {
        this.rightClickMenu.show = true;
      });
    },
  },
};
</script>
<style scoped>
.executive {
  font-weight: 400;
  font-size: 13px;
  line-height: 120%;
  text-decoration-line: underline;
}
</style>